/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * This is the entry point for Feature Hub App integration
 */
import React from 'react';
import type {
  FeatureServices,
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServiceProviderDefinition,
} from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type {
  GfaServiceConfigProviderV1,
  ContentServiceV1,
  TrackingService,
  RenderModeServiceV1,
  LocaleServiceV1,
  AsyncSsrManager,
  SerializedStateManager,
  VueFormatterServiceInterfaceV1,
  AudiFootnoteReferenceServiceInterfaceV3,
  AudiFootnoteRefernceServiceScopeManagerInterfaceV3,
} from './feature-service-types';
import { fetchCaeEnforcement } from './marketCompliance';
import { runSsrWltpPrecheck } from './runSsrWltpPrecheck';
import type { ReactFeatureApp } from '@feature-hub/react';
import { FootnoteContextProvider, InViewContextProvider } from '@oneaudi/feature-app-utils';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import FeatureApp from './FeatureApp';
import { ContextProvider } from './context';
import { getDesignLibrary } from '../alpha-helper';
import type { AudiFootnoteReferenceServiceInterface } from '@oneaudi/footnote-reference-service';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-content-service'?: ContentServiceV1;
  readonly 'dbad:audi-vue-formatter-service'?: VueFormatterServiceInterfaceV1;
  readonly 'audi-footnote-reference-service': FeatureServiceProviderDefinition<AudiFootnoteReferenceServiceInterface>;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
  readonly 'gfa:service-config-provider'?: GfaServiceConfigProviderV1;
}

export type SSRContent = any | undefined;

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.0 || ^17.0.2 || ^18.x.x',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },

  create: (env: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const { featureServices, featureAppId } = env;
    const ssrState: {
      initialWltpCompliance?: boolean;
      isMarketComplianceEnabled?: boolean;
      wltpCaeLogs?: string[];
    } = {};
    const loggerService = featureServices['s2:logger'];
    const contentService = featureServices['audi-content-service'];
    const localeService = featureServices['locale-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];

    const providerfootnoteService = featureServices[
      'audi-footnote-reference-service'
    ] as unknown as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;

    const footnoteReferenceService: AudiFootnoteReferenceServiceInterfaceV3 =
      providerfootnoteService?.getDefaultScopeRefService();

    // SSR
    // --------------------------------------------------------------------
    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    const gfaServiceConfigProvider = featureServices['gfa:service-config-provider'];
    const serviceLocale = featureServices['locale-service'];

    const serviceContent = featureServices['audi-content-service'];

    if (!asyncSSRManager) {
      try {
        const raw = serializedStateManager?.getSerializedState();
        if (raw) {
          const parsed = JSON.parse(raw) as {
            content?: Record<string, unknown>;
            isMarketComplianceEnabled?: boolean;
            initialWltpCompliance?: boolean;
            wltpCaeLogs?: string[];
          };
          ssrState.isMarketComplianceEnabled = parsed.isMarketComplianceEnabled;
          ssrState.initialWltpCompliance = parsed.initialWltpCompliance;
          ssrState.wltpCaeLogs = parsed.wltpCaeLogs;
        }
      } catch {
        // fail-open: if deserialization fails, CSR will run its own compliance check
      }
    }
    // on the server
    if (asyncSSRManager) {
      // This 3 step process inspired by this link:
      // https://github.com/oneaudi/i18n-context?tab=readme-ov-file#featureapp-definition

      const waitForThese = async () => {
        const { caeDataRequired } = await fetchCaeEnforcement({ gfaServiceConfigProvider });
        ssrState.isMarketComplianceEnabled = caeDataRequired;
        ssrState.wltpCaeLogs = [];
        const content = (await serviceContent?.getContent()) as Record<string, unknown>;
        ssrState.initialWltpCompliance = await runSsrWltpPrecheck({
          isMarketComplianceEnabled: caeDataRequired,
          content,
          vueFormatterService,
          serviceLocale,
          logger: loggerService ?? console,
          onCaeMissingLog: (message) => {
            ssrState.wltpCaeLogs?.push(message);
          },
        });
        serializedStateManager?.register(() =>
          JSON.stringify({
            content,
            isMarketComplianceEnabled: ssrState.isMarketComplianceEnabled,
            initialWltpCompliance: ssrState.initialWltpCompliance,
            wltpCaeLogs: ssrState.wltpCaeLogs,
          }),
        );
      };
      asyncSSRManager.scheduleRerender(waitForThese());
    }

    return {
      render: () => {
        return (
          <InViewContextProvider logger={loggerService}>
            <UniversalEditorProvider
              contentService={featureServices['audi-content-service']!}
              renderModeService={featureServices['audi-render-mode-service']}
            >
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <ContextProvider
                  featureAppId={featureAppId}
                  localeService={localeService}
                  vueFormatterService={vueFormatterService!}
                  contentService={contentService!}
                  footnoteReferenceService={providerfootnoteService}
                  loggerService={loggerService}
                >
                  <div data-fefa-custom-id={footnoteReferenceService?.getConsumerId()}>
                    <FeatureApp
                      trackingService={featureServices['audi-tracking-service']}
                      loggerService={loggerService}
                      localeService={localeService}
                      isMarketComplianceEnabled={ssrState.isMarketComplianceEnabled}
                      initialWltpCompliance={ssrState.initialWltpCompliance}
                      initialWltpLogs={ssrState.wltpCaeLogs}
                    />
                  </div>
                </ContextProvider>
              </FootnoteContextProvider>
            </UniversalEditorProvider>
          </InViewContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
